/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Hidden,
  Tooltip,
} from '@material-ui/core';

import { RouteComponentProps, useLocation } from 'react-router-dom';

import PersonIcon from '@material-ui/icons/Person';
import makeStyles from '@material-ui/core/styles/makeStyles';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Snackbar, { SnackbarCloseReason } from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import {
  NavigationHeader,
  responsivePadding,
  responsiveWidth,
  StepListItem,
  LoginModal,
  WatHebIkIcon,
  BereikenIcon,
  DocIcon,
} from '@myfaro/ui-react';
import { useTranslation } from 'react-i18next';
import { string } from 'yup';
import { useAppContext } from '../../core/context';
import { useProfileContext } from '../../core/profile/ProfileProvider';
import { useRefreshToken } from '../../query';
import { Profile, InfoMsg } from '../../core/types';
import { addBaseName } from '../../helpers';

interface StylesProps {
  desktopImg: string;
}

const useStyles = makeStyles(theme => ({
  page: {
    backgroundColor: theme.palette.background.default,
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: '200px',
    objectFit: 'cover',
    objectPosition: '50% 50%',
    borderRadius: '8px',
    [theme.breakpoints.up('sm')]: {
      objectPosition: '50% 12%',
    },
  },
  fullHeight: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    ...responsivePadding(theme),
    ...responsiveWidth(theme),
  },
  desktopImage: {
    backgroundImage: ({ desktopImg }: StylesProps) => `url(${desktopImg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: '59% 50%',
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      backgroundPosition: '41% 50%',
    },
  },
  content: {
    ...responsivePadding(theme, true),
  },
}));

export default (props: RouteComponentProps) => {
  const { t } = useTranslation();
  const location: any = useLocation();
  const { appConfig } = useAppContext();
  const { history } = props;
  const {
    useCurrentStep,
    onChange,
    useProfile,
    setExistingProfile,
  } = useProfileContext();
  const profile = useProfile<Profile>();

  const { currentStep } = useCurrentStep();
  const { logoSrc, heroImgSrc, desktopImg } = appConfig;
  const classes = useStyles({ desktopImg: addBaseName(desktopImg) });
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [email, setEmail] = useState('');
  const {
    refreshUserToken,
    isSuccess: isSubmitSuccess,
    error: submitError,
  } = useRefreshToken();

  const [isTouched, setIsTouched] = useState({
    email: false,
  });
  const [infoMsg, setInfoMsg] = useState<InfoMsg>({
    show: false,
    type: 'error',
    msg: t('mail.error'),
  });

  useEffect(() => {
    if (location.state?.type === 'prefillEmail') {
      setEmail(profile.who.email || profile.contact.email);
      setShowLoginDialog(true);
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccess) {
      setInfoMsg({
        show: true,
        type: 'success',
        msg: t('email.successfullySent'),
      });
    }

    if (submitError) {
      if (submitError.title === 'Not found') {
        setInfoMsg({
          show: true,
          type: 'error',
          msg: t('mail.error.noUser'),
        });
      } else {
        setInfoMsg({
          show: true,
          type: 'error',
          msg: t('mail.error.unknown'),
        });
      }
    }
  }, [isSubmitSuccess, submitError]);

  const steps = [
    {
      title: t('step.who'),
      key: '1',
      icon: <PersonIcon />,
      checked: profile.who?.isCompleted,
    },
    {
      title: t('step.what'),
      key: '2',
      icon: <WatHebIkIcon />,
      checked: profile.what?.isCompleted,
    },
    {
      title: t('step.goals'),
      key: '3',
      icon: <BereikenIcon />,
      checked: profile.goals?.isCompleted,
    },
    {
      title: t('step.result'),
      key: '4',
      icon: <DocIcon />,
      checked: !!profile.result,
    },
  ];

  const navigateForward = () => {
    history.push(`/${currentStep === 'who' ? 'who' : currentStep}`);
  };

  const resetLead = () => {
    setExistingProfile({});
  };

  const buttonLabel = () => {
    if (currentStep === 'who') {
      return t('button.startAnalysis');
    }
    // replace this with result later
    if (currentStep === 'result') {
      return t('button.goToResults');
    }
    return t('button.continueAnalysis');
  };

  const handleClose = (reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setInfoMsg(state => {
      return {
        ...state,
        show: false,
      };
    });
  };

  return (
    <Box className={classes.fullHeight}>
      <Snackbar
        open={infoMsg.show}
        autoHideDuration={6000}
        onClose={(event, reason) => handleClose(reason)}>
        <MuiAlert
          elevation={6}
          onClose={() => handleClose()}
          variant='filled'
          severity={infoMsg.type}>
          {infoMsg.msg}
        </MuiAlert>
      </Snackbar>
      <LoginModal
        isDialogOpen={showLoginDialog}
        title={t('loginModal.title')}
        subTitle={t('loginModal.subtitle')}
        inputLabel={t('loginModal.inputLabel')}
        inputValue={email}
        emailError={isTouched.email && !string().email().isValidSync(email)}
        helperEmailText={
          isTouched.email &&
          !string().email().isValidSync(email) &&
          t('errors.required.email')
        }
        buttonLabel={t('loginModal.buttonLabel')}
        disableSubmit={email === '' || !string().email().isValidSync(email)}
        errorText={undefined}
        onEmailChange={(value: string) => setEmail(value)}
        onEmailBlur={() =>
          !isTouched.email &&
          setIsTouched(state => ({
            ...state,
            email: true,
          }))
        }
        onSubmit={() => {
          refreshUserToken(email);
          setShowLoginDialog(false);
        }}
        onClose={() => setShowLoginDialog(false)}
      />
      <Grid container>
        <Grid className={classes.desktopImage} item md={6} />
        <Grid className={classes.content} item xs={12} md={6}>
          <NavigationHeader
            showHeader
            showChildren
            logoSrc={addBaseName(logoSrc)}>
            {currentStep !== 'who' && (
              <Button variant='outlined' color='primary'>
                {/* @ts-ignore */}
                <Tooltip title={t('resetSimulation')}>
                  <RotateLeftIcon onClick={resetLead} />
                </Tooltip>
              </Button>
            )}
          </NavigationHeader>
          <Hidden mdUp>
            <Box mb={2}>
              <img
                className={classes.image}
                src={addBaseName(heroImgSrc)}
                alt='hero-Img'
              />
            </Box>
          </Hidden>
          <Box mb={2}>
            <Typography variant='h4'>{t('landingspage.title')}</Typography>
          </Box>
          <Box mb={3}>
            <Typography variant='body2' color='textSecondary'>
              {t('home.text1')}
            </Typography>
          </Box>
          <Box mb={2}>
            <Typography variant='subtitle2'>
              {t('landingspage.4steps')}
            </Typography>
          </Box>
          {steps.map(({ checked, ...props }, index) => (
            <StepListItem
              index={index.toString()}
              checked={checked}
              {...props}
            />
          ))}
          <Box mt={4}>
            <Box display='flex' justifyContent='center'>
              <Button
                endIcon={<NavigateNextIcon />}
                color='primary'
                variant='contained'
                onClick={navigateForward}>
                {buttonLabel()}
              </Button>
            </Box>
            <Box mt={1} display='flex' justifyContent='center'>
              <Button onClick={() => setShowLoginDialog(true)}>
                {t('button.openPreviousAnalysis')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
